import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { employeeAPI, helpdeskAPI, systemAPI } from '../services/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { AlertTriangle, LifeBuoy, Plus, Send, Upload, CircleDot, Gauge, CheckCircle2, Archive, Ticket, ListChecks, ShieldAlert, Timer, MessageSquare, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from './ui/table-controls';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { EmptyState, StatSkeletonGrid, CardSkeleton } from './common/States';
import { StatCard } from './common/StatCard';

const STATUS_COLUMNS = ['open', 'in_progress', 'waiting_on_requester', 'resolved', 'closed'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];

const statusMeta = {
  open: { label: 'Open', icon: CircleDot },
  in_progress: { label: 'In Progress', icon: Gauge },
  waiting_on_requester: { label: 'Waiting on Requester', icon: Gauge },
  resolved: { label: 'Resolved', icon: CheckCircle2 },
  closed: { label: 'Closed', icon: Archive },
};

const HelpdeskModule = ({ mode = 'employee' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState({ summary: {}, board: {} });
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [detailView, setDetailView] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [featureBanner, setFeatureBanner] = useState('');


  const [createForm, setCreateForm] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });
  const [commentText, setCommentText] = useState('');
  const [commentVisibility, setCommentVisibility] = useState('requester');
  const [attachmentFile, setAttachmentFile] = useState(null);

  const canManage = useMemo(() => ['admin', 'workspace_admin', 'hr_admin', 'manager', 'hr_manager'].includes(user?.role), [user?.role]);
  const canAdmin = useMemo(() => ['admin', 'workspace_admin', 'hr_admin'].includes(user?.role), [user?.role]);

  const [adminCategoryForm, setAdminCategoryForm] = useState({ name: '', description: '' });
  const [slaLocalPolicies, setSlaLocalPolicies] = useState([
    { priority: 'low', first_response_minutes: 1440, resolution_minutes: 7200 },
    { priority: 'medium', first_response_minutes: 480, resolution_minutes: 4320 },
    { priority: 'high', first_response_minutes: 240, resolution_minutes: 1440 },
    { priority: 'critical', first_response_minutes: 60, resolution_minutes: 480 },
  ]);
  const [slaSaveLoading, setSlaSaveLoading] = useState(false);

  const [queueFilters, setQueueFilters] = useState({
    status: '', priority: '', category: '', assignee_id: '',
    sort_by: 'created_at', sort_dir: 'desc', sla_state: '', assigned_to_me: false,
    created_from: '', created_to: ''
  });
  const [queueRawData, setQueueRawData] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);

  const fetchQueueData = async (filters) => {
    setQueueLoading(true);
    try {
      const q = { page: 1, page_size: 200 };
      if (filters.status && filters.status !== 'all') q.status = filters.status;
      if (filters.priority && filters.priority !== 'all') q.priority = filters.priority;
      if (filters.category) q.category = filters.category;
      if (filters.assignee_id) q.assignee_id = filters.assignee_id;
      if (filters.sla_state) q.sla_state = filters.sla_state;
      if (filters.assigned_to_me) q.assigned_to_me = filters.assigned_to_me;
      if (filters.sort_by) q.sort_by = filters.sort_by;
      if (filters.sort_dir) q.sort_dir = filters.sort_dir;
      if (filters.created_from) q.created_from = filters.created_from;
      if (filters.created_to) q.created_to = filters.created_to;
      const res = await helpdeskAPI.getTickets(q);
      setQueueRawData(res.data.items || []);
    } catch {
      setQueueRawData([]);
    } finally {
      setQueueLoading(false);
    }
  };

  const queueTable = useTableControls(queueRawData, { initialPageSize: 25 });

  useEffect(() => {
    if (canManage) fetchQueueData(queueFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueFilters, canManage]);


  const fetchData = async () => {
    try {
      setLoading(true);
      const [boardRes, ticketsRes] = await Promise.all([
        helpdeskAPI.getBoard(),
        helpdeskAPI.getTickets({ page: 1, page_size: 200 }),
      ]);
      setBoard(boardRes.data || { summary: {}, board: {} });
      setTickets(ticketsRes.data?.items || []);
      try {
        const categoriesRes = await helpdeskAPI.getCategories();
        setCategories(categoriesRes.data || []);
      } catch (error) {
        // Backward compatibility: if category master endpoint/table is not ready, fall back to defaults.
        setCategories([
          { id: 'general', name: 'general' },
          { id: 'attendance', name: 'attendance' },
          { id: 'leave', name: 'leave' },
          { id: 'payroll', name: 'payroll' },
        ]);
      }
      if (canManage) {
        const empRes = await employeeAPI.list();
        setEmployees(empRes.data || []);
      }
    } catch (error) {
      if (!featureBanner) {
        toast.error('Failed to load helpdesk module');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    try {
      const [detailRes, auditRes] = await Promise.all([
        helpdeskAPI.getTicketDetail(ticketId),
        canManage ? helpdeskAPI.getTicketAudit(ticketId) : Promise.resolve({ data: [] }),
      ]);
      setSelectedTicketId(ticketId);
      setSelectedTicketDetail(detailRes.data);
      setAuditEvents(auditRes.data || []);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const health = await systemAPI.getFeatureHealth();
        if (health.data?.features?.helpdesk?.migration_required) {
          setFeatureBanner('Helpdesk setup pending. Please run backend migration and restart service.');
        } else {
          setFeatureBanner('');
        }
      } catch (_) {
        setFeatureBanner('');
      }
      fetchData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateTicket = async () => {
    if (!createForm.subject || !createForm.description) {
      toast.error('Subject and description are required');
      return;
    }
    try {
      await helpdeskAPI.createTicket(createForm);
      toast.success('Ticket created');
      setShowCreateDialog(false);
      setCreateForm({ subject: '', description: '', category: 'general', priority: 'medium' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleUpdateTicket = async (data) => {
    if (!selectedTicketId) return;
    try {
      await helpdeskAPI.updateTicket(selectedTicketId, data);
      toast.success('Ticket updated');
      await loadTicketDetail(selectedTicketId);
      fetchData();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleAddComment = async () => {
    if (!selectedTicketId || !commentText.trim()) return;
    try {
      await helpdeskAPI.addComment(selectedTicketId, {
        comment: commentText,
        visibility: canManage ? commentVisibility : 'requester',
      });
      setCommentText('');
      setCommentVisibility('requester');
      await loadTicketDetail(selectedTicketId);
      fetchData();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleUploadAttachment = async () => {
    if (!selectedTicketId || !attachmentFile) return;
    try {
      const formData = new FormData();
      formData.append('file', attachmentFile);
      await helpdeskAPI.uploadAttachment(selectedTicketId, formData);
      setAttachmentFile(null);
      toast.success('Attachment uploaded');
      await loadTicketDetail(selectedTicketId);
    } catch (error) {
      toast.error('Failed to upload attachment');
    }
  };

  const handleEscalate = async () => {
    if (!selectedTicketId) return;
    try {
      await helpdeskAPI.escalateTicket(selectedTicketId, { notes: 'Escalated from dashboard' });
      toast.success('Ticket escalated');
      await loadTicketDetail(selectedTicketId);
      fetchData();
    } catch (error) {
      toast.error('Failed to escalate ticket');
    }
  };

  const handleExportTickets = async () => {
    try {
      // HD-502: pass active queue filter state so CSV matches queue view
      const params = {};
      if (queueFilters.status && queueFilters.status !== 'all') params.status = queueFilters.status;
      if (queueFilters.priority && queueFilters.priority !== 'all') params.priority = queueFilters.priority;
      if (queueFilters.category) params.category = queueFilters.category;
      if (queueFilters.assignee_id) params.assignee_id = queueFilters.assignee_id;
      if (queueFilters.sla_state) params.sla_state = queueFilters.sla_state;
      if (queueFilters.assigned_to_me) params.assigned_to_me = queueFilters.assigned_to_me;
      if (queueFilters.created_from) params.created_from = queueFilters.created_from;
      if (queueFilters.created_to) params.created_to = queueFilters.created_to;
      const response = await helpdeskAPI.exportTickets(params);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `helpdesk-export-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Helpdesk export downloaded');
    } catch (error) {
      toast.error('Failed to export helpdesk tickets');
    }
  };

  const handleCreateCategory = async () => {
    if (!adminCategoryForm.name.trim()) return toast.error('Category name required');
    try {
      await helpdeskAPI.createCategory({ name: adminCategoryForm.name.trim(), description: adminCategoryForm.description.trim() });
      setAdminCategoryForm({ name: '', description: '' });
      const res = await helpdeskAPI.getCategories();
      setCategories(res.data || []);
      toast.success('Category created');
    } catch { toast.error('Failed to create category'); }
  };

  const handleToggleCategory = async (cat) => {
    try {
      await helpdeskAPI.updateCategory(cat.id, { is_active: !cat.is_active });
      const res = await helpdeskAPI.getCategories();
      setCategories(res.data || []);
      toast.success(`Category ${cat.is_active ? 'deactivated' : 'activated'}`);
    } catch { toast.error('Failed to update category'); }
  };

  const handleSaveSlaPolicy = async () => {
    setSlaSaveLoading(true);
    try {
      await helpdeskAPI.updateSlaPolicies(slaLocalPolicies);
      toast.success('SLA policies saved');
    } catch { toast.error('Failed to save SLA policies'); }
    finally { setSlaSaveLoading(false); }
  };

  const detailTickets = useMemo(() => {
    if (!detailView || detailView.key === 'total') return tickets;
    if (detailView.type === 'status') {
      return tickets.filter((ticket) => ticket.status === detailView.key);
    }
    if (detailView.type === 'sla') {
      return tickets.filter((ticket) => ticket.sla_state === detailView.key);
    }
    return tickets;
  }, [detailView, tickets]);
  const detailTicketsTable = useTableControls(detailTickets, {
    initialSortKey: 'updated_at',
    initialSortDirection: 'desc',
  });

  const openDetailView = async (view) => {
    setDetailView(view);
    const scopedTickets = (() => {
      if (view.key === 'total') return tickets;
      if (view.type === 'status') return tickets.filter((ticket) => ticket.status === view.key);
      if (view.type === 'sla') return tickets.filter((ticket) => ticket.sla_state === view.key);
      return tickets;
    })();
    if (scopedTickets.length > 0) {
      await loadTicketDetail(scopedTickets[0].id);
    } else {
      setSelectedTicketId(null);
      setSelectedTicketDetail(null);
      setAuditEvents([]);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <StatSkeletonGrid count={4} />
        <div className="grid gap-6 xl:grid-cols-3">
          <CardSkeleton rows={6} className="xl:col-span-2" />
          <CardSkeleton rows={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {featureBanner && (
        <Card className="border-warning/40 bg-warning/12">
          <CardContent className="pt-4 text-hr-sm text-amber-600 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {featureBanner}
          </CardContent>
        </Card>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-hr-h1 font-bold tracking-tight flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-primary" />
            Helpdesk
          </h1>
          <p className="text-hr-sm text-muted-foreground">
            Ticketing, SLA tracking, assignment, comments, and escalation board.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button variant="outline" onClick={() => navigate('/admin/import/helpdesk_category')}>
              Import Categories
            </Button>
          )}
          {canManage && (
            <Button variant="outline" onClick={handleExportTickets}>
              Export CSV
            </Button>
          )}
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Ticket
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Helpdesk Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Subject</Label>
                <Input value={createForm.subject} onChange={(e) => setCreateForm({ ...createForm, subject: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={4} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={createForm.category} onValueChange={(value) => setCreateForm({ ...createForm, category: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id || category.name} value={category.name}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={createForm.priority} onValueChange={(value) => setCreateForm({ ...createForm, priority: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((priority) => (
                        <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full" onClick={handleCreateTicket}>Submit Ticket</Button>
            </div>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4 xl:grid-cols-7">
        <StatCard
          label="Total"
          value={board.summary?.total || 0}
          icon={Ticket}
          tone="primary"
          onClick={() => openDetailView({ key: 'total', type: 'all', label: 'All Tickets' })}
          className={detailView?.key === 'total' ? 'ring-2 ring-primary/40' : ''}
        />
        <StatCard
          label="Open"
          value={board.summary?.open || 0}
          icon={CircleDot}
          tone="info"
          onClick={() => openDetailView({ key: 'open', type: 'status', label: 'Open Tickets' })}
          className={detailView?.key === 'open' ? 'ring-2 ring-primary/40' : ''}
        />
        <StatCard
          label="In Progress"
          value={board.summary?.in_progress || 0}
          icon={Gauge}
          tone="warning"
          onClick={() => openDetailView({ key: 'in_progress', type: 'status', label: 'In Progress Tickets' })}
          className={detailView?.key === 'in_progress' ? 'ring-2 ring-primary/40' : ''}
        />
        <StatCard
          label="Resolved"
          value={board.summary?.resolved || 0}
          icon={CheckCircle2}
          tone="success"
          onClick={() => openDetailView({ key: 'resolved', type: 'status', label: 'Resolved Tickets' })}
          className={detailView?.key === 'resolved' ? 'ring-2 ring-primary/40' : ''}
        />
        <StatCard
          label="At Risk"
          value={board.summary?.at_risk || 0}
          icon={ShieldAlert}
          tone="warning"
          onClick={() => openDetailView({ key: 'at_risk', type: 'sla', label: 'At Risk Tickets' })}
          className={detailView?.key === 'at_risk' ? 'ring-2 ring-primary/40' : ''}
        />
        <StatCard
          label="Breached"
          value={board.summary?.breached || 0}
          icon={AlertTriangle}
          tone="danger"
          onClick={() => openDetailView({ key: 'breached', type: 'sla', label: 'Breached Tickets' })}
          className={detailView?.key === 'breached' ? 'ring-2 ring-primary/40' : ''}
        />
        <StatCard
          label="Avg Resolution"
          value={board.summary?.avg_resolution_hours != null ? board.summary.avg_resolution_hours : '—'}
          suffix={board.summary?.avg_resolution_hours != null ? 'h' : ''}
          icon={Timer}
          tone="slate"
        />
      </div>

      {detailView && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-hr-lg">{detailView.label} - Detailed Page</CardTitle>
              <Button variant="outline" onClick={() => setDetailView(null)}>Close Detail View</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-hr-sm">
                <thead>
                  <tr>
                    <th className="p-2 text-left">Ticket</th>
                    <th className="p-2 text-left">Subject</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Priority</th>
                    <th className="p-2 text-left">SLA</th>
                    <th className="p-2 text-left">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {detailTicketsTable.data.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={() => loadTicketDetail(ticket.id)}
                      className={`cursor-pointer hover:bg-muted/40 ${selectedTicketId === ticket.id ? 'bg-muted/40' : ''}`}
                    >
                      <td className="p-2 font-medium">{ticket.ticket_number}</td>
                      <td className="p-2">{ticket.subject}</td>
                      <td className="p-2 capitalize">{(ticket.status || '').replace('_', ' ')}</td>
                      <td className="p-2 capitalize">{ticket.priority}</td>
                      <td className="p-2 capitalize">{(ticket.sla_state || '').replace('_', ' ')}</td>
                      <td className="p-2">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                  {detailTicketsTable.data.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <EmptyState compact icon={Ticket} title="No tickets found" description="There are no tickets matching this card." />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="pt-3">
              <TableControls
                totalRows={detailTicketsTable.totalRows}
                page={detailTicketsTable.page}
                totalPages={detailTicketsTable.totalPages}
                pageSize={detailTicketsTable.pageSize}
                pageSizes={detailTicketsTable.pageSizes}
                onPageSizeChange={detailTicketsTable.onPageSizeChange}
                onPrevious={detailTicketsTable.goToPreviousPage}
                onNext={detailTicketsTable.goToNextPage}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          {canManage ? (
            <Tabs defaultValue="board" className="w-full p-1">
              <div className="px-4 pt-4">
                <TabsList>
                  <TabsTrigger value="board">Status Board</TabsTrigger>
                  <TabsTrigger value="queue">Agent Queue</TabsTrigger>
                  {canAdmin && <TabsTrigger value="admin">Admin</TabsTrigger>}
                </TabsList>
              </div>

              <TabsContent value="board" className="px-4 pb-4">
                <CardHeader className="px-0 pt-3">
                  <CardTitle className="text-hr-lg">Status Board</CardTitle>
                </CardHeader>
                <CardContent className="px-0">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {STATUS_COLUMNS.map((status) => (
                      <div key={status} className="rounded-lg border p-3 space-y-2">
                        <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-background rounded">
                          <p className="text-hr-sm font-semibold capitalize flex items-center gap-1.5">
                            {React.createElement(statusMeta[status]?.icon || CircleDot, { className: 'h-3.5 w-3.5 text-primary' })}
                            {statusMeta[status]?.label || status.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                          {(board.board?.[status] || []).map((ticket) => (
                            <button
                              type="button"
                              key={ticket.id}
                              onClick={() => loadTicketDetail(ticket.id)}
                              className={`w-full text-left rounded-md border p-2 hover:bg-muted/40 ${selectedTicketId === ticket.id ? 'border-primary' : ''}`}
                            >
                              <p className="text-hr-xs font-medium">{ticket.ticket_number}</p>
                              <p className="text-hr-sm font-semibold line-clamp-2">{ticket.subject}</p>
                              <div className="mt-2 flex gap-1 flex-wrap">
                                <Badge variant="outline">{ticket.priority}</Badge>
                                <Badge variant={ticket.sla_state === 'breached' ? 'danger' : ticket.sla_state === 'at_risk' ? 'warning' : 'success'}>
                                  {ticket.sla_state}
                                </Badge>
                              </div>
                            </button>
                          ))}
                          {(board.board?.[status] || []).length === 0 && (
                            <p className="text-hr-xs text-muted-foreground">No tickets</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </TabsContent>

              <TabsContent value="queue" className="px-4 pb-4">
                <CardHeader className="px-0 pt-3 border-b pb-4 mb-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <CardTitle className="text-hr-lg">Agent Queue</CardTitle>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => setQueueFilters({...queueFilters, assigned_to_me: true, status: 'open', assignee_id: '', sla_state: ''})}>My Open</Button>
                      <Button variant="outline" size="sm" onClick={() => setQueueFilters({...queueFilters, assignee_id: 'unassigned', status: 'open', assigned_to_me: false, sla_state: ''})}>Unassigned</Button>
                      <Button variant="outline" size="sm" onClick={() => setQueueFilters({...queueFilters, status: 'open', sla_state: 'breached', assignee_id: '', assigned_to_me: false})}>Overdue</Button>
                      <Button variant="ghost" size="sm" onClick={() => setQueueFilters({status:'',priority:'',category:'',assignee_id:'',sort_by:'created_at',sort_dir:'desc',sla_state:'',assigned_to_me:false,created_from:'',created_to:''})}>Clear</Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                    <Select value={queueFilters.status} onValueChange={(v) => setQueueFilters({...queueFilters, status: v})}>
                      <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {STATUS_COLUMNS.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={queueFilters.priority} onValueChange={(v) => setQueueFilters({...queueFilters, priority: v})}>
                      <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input value={queueFilters.created_from} onChange={(e) => setQueueFilters({...queueFilters, created_from: e.target.value})} type="date" />
                    <Input value={queueFilters.created_to} onChange={(e) => setQueueFilters({...queueFilters, created_to: e.target.value})} type="date" />
                    <Select value={queueFilters.sort_by} onValueChange={(v) => setQueueFilters({...queueFilters, sort_by: v})}>
                      <SelectTrigger><SelectValue placeholder="Sort By" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="created_at">Created At</SelectItem>
                        <SelectItem value="priority">Priority</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                        <SelectItem value="sla_state">SLA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent className="px-0">
                  <div className="overflow-x-auto rounded-md border text-hr-sm">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="p-2 text-left">Ticket #</th>
                          <th className="p-2 text-left">Subject</th>
                          <th className="p-2 text-left">Status</th>
                          <th className="p-2 text-left">Priority</th>
                          <th className="p-2 text-left">SLA</th>
                          <th className="p-2 text-left cursor-pointer select-none" onClick={() => setQueueFilters({...queueFilters, sort_dir: queueFilters.sort_dir === 'asc' ? 'desc' : 'asc'})}>Created {queueFilters.sort_dir === 'asc' ? '↑' : '↓'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {queueTable.data.map((ticket) => (
                          <tr key={ticket.id} onClick={() => loadTicketDetail(ticket.id)} className={`cursor-pointer hover:bg-muted/40 ${selectedTicketId === ticket.id ? 'bg-muted/40' : ''}`}>
                            <td className="p-2 font-medium">{ticket.ticket_number}</td>
                            <td className="p-2 line-clamp-1 max-w-[200px]">{ticket.subject}</td>
                            <td className="p-2 capitalize">{(ticket.status || '').replace(/_/g, ' ')}</td>
                            <td className="p-2 capitalize">{ticket.priority}</td>
                            <td className="p-2">
                              <Badge variant={ticket.sla_state === 'breached' ? 'danger' : ticket.sla_state === 'at_risk' ? 'warning' : 'success'}>
                                {ticket.sla_state}
                              </Badge>
                            </td>
                            <td className="p-2">{ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : '-'}</td>
                          </tr>
                        ))}
                        {queueTable.data.length === 0 && (
                          <tr><td colSpan={6} className="p-0">
                            {queueLoading ? (
                              <div className="p-4"><CardSkeleton rows={5} className="border-0 bg-transparent p-0" /></div>
                            ) : (
                              <EmptyState compact icon={Inbox} title="No tickets in queue" description="Adjust filters or check back later for new tickets." />
                            )}
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4">
                    <TableControls
                      totalRows={queueTable.totalRows}
                      page={queueTable.page}
                      totalPages={queueTable.totalPages}
                      pageSize={queueTable.pageSize}
                      pageSizes={queueTable.pageSizes}
                      onPageSizeChange={queueTable.onPageSizeChange}
                      onPrevious={queueTable.goToPreviousPage}
                      onNext={queueTable.goToNextPage}
                    />
                  </div>
                </CardContent>
              </TabsContent>

              {canAdmin && (
                <TabsContent value="admin" className="px-4 pb-4">
                  <div className="space-y-6 pt-3">

                    {/* HD-503: Category Management */}
                    <div>
                      <h3 className="text-hr-base font-semibold mb-3">Category Management</h3>
                      <div className="overflow-x-auto rounded-md border text-hr-sm mb-3">
                        <table className="w-full">
                          <thead className="bg-muted/50 border-b">
                            <tr>
                              <th className="p-2 text-left">Name</th>
                              <th className="p-2 text-left">Status</th>
                              <th className="p-2 text-left">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categories.map((cat) => (
                              <tr key={cat.id || cat.name} className="border-b last:border-0">
                                <td className="p-2 font-medium capitalize">{cat.name}</td>
                                <td className="p-2">
                                  <Badge variant={cat.is_active !== false ? 'success' : 'outline'}>
                                    {cat.is_active !== false ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td className="p-2">
                                  {cat.id && (
                                    <Button variant="outline" size="sm" onClick={() => handleToggleCategory(cat)}>
                                      {cat.is_active !== false ? 'Deactivate' : 'Activate'}
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {categories.length === 0 && (
                              <tr><td colSpan={3} className="p-0">
                                <EmptyState compact icon={ListChecks} title="No categories" description="Add a category below to organise incoming tickets." />
                              </td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-hr-xs mb-1 block">New Category Name</Label>
                          <Input value={adminCategoryForm.name} onChange={(e) => setAdminCategoryForm({...adminCategoryForm, name: e.target.value})} placeholder="e.g. Benefits" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-hr-xs mb-1 block">Description (optional)</Label>
                          <Input value={adminCategoryForm.description} onChange={(e) => setAdminCategoryForm({...adminCategoryForm, description: e.target.value})} placeholder="Short description" />
                        </div>
                        <Button onClick={handleCreateCategory}>Add Category</Button>
                      </div>
                    </div>

                    {/* HD-503: SLA Policy Config */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-hr-base font-semibold">SLA Policies</h3>
                        <Button size="sm" onClick={handleSaveSlaPolicy} disabled={slaSaveLoading}>
                          {slaSaveLoading ? 'Saving…' : 'Save SLA Policies'}
                        </Button>
                      </div>
                      <div className="overflow-x-auto rounded-md border text-hr-sm">
                        <table className="w-full">
                          <thead className="bg-muted/50 border-b">
                            <tr>
                              <th className="p-2 text-left">Priority</th>
                              <th className="p-2 text-left">First Response (min)</th>
                              <th className="p-2 text-left">Resolution (min)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {slaLocalPolicies.map((pol, idx) => (
                              <tr key={pol.priority} className="border-b last:border-0">
                                <td className="p-2 capitalize font-medium">{pol.priority}</td>
                                <td className="p-2">
                                  <Input
                                    type="number" min={1}
                                    value={pol.first_response_minutes}
                                    onChange={(e) => setSlaLocalPolicies(prev => prev.map((p, i) => i === idx ? {...p, first_response_minutes: Number(e.target.value)} : p))}
                                    className="w-24"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number" min={1}
                                    value={pol.resolution_minutes}
                                    onChange={(e) => setSlaLocalPolicies(prev => prev.map((p, i) => i === idx ? {...p, resolution_minutes: Number(e.target.value)} : p))}
                                    className="w-24"
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                  </div>
                </TabsContent>
              )}
            </Tabs>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="text-hr-lg">Status Board</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {STATUS_COLUMNS.map((status) => (
                    <div key={status} className="rounded-lg border p-3 space-y-2">
                      <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-background rounded">
                        <p className="text-hr-sm font-semibold capitalize flex items-center gap-1.5">
                          {React.createElement(statusMeta[status]?.icon || CircleDot, { className: 'h-3.5 w-3.5 text-primary' })}
                          {statusMeta[status]?.label || status.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                        {(board.board?.[status] || []).map((ticket) => (
                          <button
                            type="button"
                            key={ticket.id}
                            onClick={() => loadTicketDetail(ticket.id)}
                            className={`w-full text-left rounded-md border p-2 hover:bg-muted/40 ${selectedTicketId === ticket.id ? 'border-primary' : ''}`}
                          >
                            <p className="text-hr-xs font-medium">{ticket.ticket_number}</p>
                            <p className="text-hr-sm font-semibold line-clamp-2">{ticket.subject}</p>
                            <div className="mt-2 flex gap-1 flex-wrap">
                              <Badge variant="outline">{ticket.priority}</Badge>
                              <Badge variant={ticket.sla_state === 'breached' ? 'danger' : ticket.sla_state === 'at_risk' ? 'warning' : 'success'}>
                                {ticket.sla_state}
                              </Badge>
                            </div>
                          </button>
                        ))}
                        {(board.board?.[status] || []).length === 0 && (
                          <p className="text-hr-xs text-muted-foreground">No tickets</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-hr-lg">Ticket Detail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedTicketDetail?.ticket ? (
              <EmptyState compact icon={MessageSquare} title="No ticket selected" description="Select a ticket from the board or queue to view its details." />
            ) : (
              <>
                <div className="rounded-md border p-3">
                  <p className="text-hr-xs text-muted-foreground">{selectedTicketDetail.ticket.ticket_number}</p>
                  <p className="text-hr-sm font-semibold">{selectedTicketDetail.ticket.subject}</p>
                  <p className="text-hr-xs text-muted-foreground mt-1">{selectedTicketDetail.ticket.description}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Badge variant="outline">{selectedTicketDetail.ticket.priority}</Badge>
                    <Badge variant="outline">{selectedTicketDetail.ticket.status}</Badge>
                    <Badge variant={selectedTicketDetail.ticket.sla_state === 'breached' ? 'danger' : selectedTicketDetail.ticket.sla_state === 'at_risk' ? 'warning' : 'success'}>
                      {selectedTicketDetail.ticket.sla_state}
                    </Badge>
                  </div>
                </div>

                {canManage && (
                  <div className="space-y-2 rounded-md border p-3">
                    <p className="text-hr-sm font-medium">Workflow Controls</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Select onValueChange={(value) => handleUpdateTicket({ status: value })}>
                        <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                          {STATUS_COLUMNS.map((status) => (
                            <SelectItem key={status} value={status}>{status}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select onValueChange={(value) => handleUpdateTicket({ priority: value })}>
                        <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
                        <SelectContent>
                          {PRIORITIES.map((priority) => (
                            <SelectItem key={priority} value={priority}>{priority}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Select onValueChange={(value) => handleUpdateTicket({ assigned_to_employee_id: value })}>
                      <SelectTrigger><SelectValue placeholder="Assign To Employee" /></SelectTrigger>
                      <SelectContent>
                        {employees.map((employee) => (
                          <SelectItem key={employee.employee_id} value={employee.employee_id}>
                            {employee.employee_id} - {employee.name || employee.first_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" className="w-full" onClick={handleEscalate}>
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Escalate
                    </Button>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-hr-sm font-medium">Comments</p>
                  <div className="space-y-2 max-h-[180px] overflow-y-auto">
                    {(selectedTicketDetail.comments || []).map((row) => (
                      <div key={row.id} className="rounded-md border p-2">
                        <p className="text-hr-xs text-muted-foreground">{row.author_employee_id || 'user'} • {new Date(row.created_at).toLocaleString()}</p>
                        <p className="text-hr-sm">{row.comment}</p>
                      </div>
                    ))}
                    {(selectedTicketDetail.comments || []).length === 0 && (
                      <p className="text-hr-xs text-muted-foreground">No comments yet.</p>
                    )}
                  </div>
                  {selectedTicketDetail.ticket.status === 'resolved' && !canManage && (
                    <div className="flex items-center justify-between bg-warning/12 p-2 rounded border border-warning/40 mt-2 mb-1">
                      <p className="text-hr-xs text-amber-600">
                        This ticket is resolved. Comment to auto-reopen, or reopen manually.
                      </p>
                      <Button variant="outline" size="sm" onClick={() => handleUpdateTicket({ status: 'open' })}>
                        Reopen Ticket
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add comment..." />
                    {canManage && (
                      <Select value={commentVisibility} onValueChange={setCommentVisibility}>
                        <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="requester">requester</SelectItem>
                          <SelectItem value="internal">internal</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button size="icon" onClick={handleAddComment}><Send className="h-4 w-4" /></Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-hr-sm font-medium">Attachments</p>
                  <div className="space-y-1 max-h-[120px] overflow-y-auto">
                    {(selectedTicketDetail.attachments || []).map((a) => {
                      const href = helpdeskAPI.getAttachmentDownloadUrl(a.id, a.download_token);
                      return (
                        <a
                          key={a.id}
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className="block text-hr-xs rounded border p-2 hover:bg-muted/40"
                        >
                          {a.file_name}
                        </a>
                      );
                    })}
                    {(selectedTicketDetail.attachments || []).length === 0 && (
                      <p className="text-hr-xs text-muted-foreground">No attachments.</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input type="file" onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)} />
                    <Button variant="outline" onClick={handleUploadAttachment}><Upload className="h-4 w-4 mr-1" />Upload</Button>
                  </div>
                </div>

                {canManage && (
                  <div className="space-y-2">
                    <p className="text-hr-sm font-medium">Audit Trail</p>
                    <div className="space-y-1 max-h-[120px] overflow-y-auto">
                      {auditEvents.map((event) => (
                        <div key={event.id} className="rounded border p-2 text-hr-xs">
                          <p className="font-medium">{event.event_type}</p>
                          <p className="text-muted-foreground">{new Date(event.created_at).toLocaleString()}</p>
                        </div>
                      ))}
                      {auditEvents.length === 0 && <p className="text-hr-xs text-muted-foreground">No audit events.</p>}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HelpdeskModule;
